<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  accountId: { type: Number, required: true },
  conversationId: { type: [Number, String], required: true },
  sender: { type: Object, default: null },
});

const emit = defineEmits(['resolved', 'cancel']);
const mode = ref('existing');
const query = ref('');
const candidates = ref([]);
const contactSearchCompleted = ref(false);
const customerQuery = ref('');
const customers = ref([]);
const customerSearchCompleted = ref(false);
const selectedCustomer = ref(null);
const firstName = ref('');
const lastName = ref('');
const middleName = ref('');
const position = ref('');
const loading = ref(false);
const errorMessage = ref('');
const directorySequences = new Map();

const contactPersonName = person =>
  [person.last_name, person.first_name, person.middle_name]
    .filter(Boolean)
    .join(' ') || person.display_name;

const baseParams = () => ({
  account_id: String(props.accountId),
  conversation_id: String(props.conversationId),
});

const search = async (resource, cleanValue, target, completed, currentSearch) => {
  const params = new URLSearchParams({ ...baseParams(), q: cleanValue });
  try {
    const response = await fetch(`/csm-action/api/${resource}?${params}`, {
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`status ${response.status}`);
    const payload = await response.json();
    if (directorySequences.get(resource) === currentSearch) {
      target.value = payload.items || [];
      completed.value = true;
      errorMessage.value = '';
    }
  } catch {
    if (directorySequences.get(resource) === currentSearch) {
      target.value = [];
      completed.value = false;
      errorMessage.value = 'Поиск CSM временно недоступен';
    }
  }
};

const scheduleSearch = (resource, value, target, completed, onCleanup) => {
  const currentSearch = (directorySequences.get(resource) || 0) + 1;
  directorySequences.set(resource, currentSearch);
  const cleanValue = value.trim();
  completed.value = false;
  if ([...cleanValue].length < 2) {
    target.value = [];
    errorMessage.value = '';
    return;
  }
  const timer = setTimeout(
    () => search(resource, cleanValue, target, completed, currentSearch),
    250
  );
  onCleanup(() => clearTimeout(timer));
};

watch(query, (value, _previous, onCleanup) =>
  scheduleSearch('contact-people', value, candidates, contactSearchCompleted, onCleanup)
);
watch(customerQuery, (value, _previous, onCleanup) =>
  scheduleSearch('customers', value, customers, customerSearchCompleted, onCleanup)
);
watch(mode, () => {
  errorMessage.value = '';
});

const post = async (path, body) => {
  loading.value = true;
  errorMessage.value = '';
  try {
    const response = await fetch(`/csm-action/api/${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        account_id: props.accountId,
        conversation_id: Number(props.conversationId),
        ...body,
      }),
    });
    if (!response.ok) throw new Error(`status ${response.status}`);
    emit('resolved', await response.json());
  } catch {
    errorMessage.value = 'Не удалось сохранить связь с CSM';
  } finally {
    loading.value = false;
  }
};

const linkCandidate = candidate =>
  post('contact-links', { contact_person_id: candidate.contact_person.id });

const createPerson = () => {
  if (!selectedCustomer.value || !firstName.value.trim() || !lastName.value.trim()) {
    errorMessage.value = 'Выберите клиента и укажите имя и фамилию';
    return;
  }
  post('contact-people', {
    customer_id: selectedCustomer.value.id,
    first_name: firstName.value.trim(),
    last_name: lastName.value.trim(),
    middle_name: middleName.value.trim(),
    position: position.value.trim(),
  });
};
</script>

<template>
  <section class="csm-resolution">
    <div class="csm-resolution__tabs">
      <button type="button" :class="{ active: mode === 'existing' }" @click="mode = 'existing'">
        Найти
      </button>
      <button type="button" data-testid="create-contact-mode" :class="{ active: mode === 'create' }" @click="mode = 'create'">
        Создать
      </button>
    </div>

    <template v-if="mode === 'existing'">
      <label>
        <span>Контактное лицо</span>
        <input v-model="query" data-testid="contact-person-search" type="search" placeholder="ФИО, должность или клиент" />
      </label>
      <div v-if="candidates.length" class="csm-resolution__results">
        <button
          v-for="candidate in candidates"
          :key="candidate.contact_person.id"
          type="button"
          data-testid="link-contact-person"
          :disabled="loading"
          @click="linkCandidate(candidate)"
        >
          <strong :title="contactPersonName(candidate.contact_person)">
            {{ contactPersonName(candidate.contact_person) }}
          </strong>
          <span v-if="candidate.contact_person.position">{{ candidate.contact_person.position }}</span>
          <span>{{ candidate.customer.display_name }}</span>
        </button>
      </div>
      <p v-else-if="contactSearchCompleted" class="csm-resolution__hint" data-testid="contact-person-empty">
        Контактные лица не найдены
      </p>
    </template>

    <template v-else>
      <p class="csm-resolution__hint">
        Новый контакт будет создан для выбранного клиента. Email и телефон возьмём из Chatwoot.
      </p>
      <label>
        <span>Клиент</span>
        <input v-model="customerQuery" data-testid="customer-search" type="search" placeholder="Название, ФИО или ИНН" />
      </label>
      <div v-if="customers.length && !selectedCustomer" class="csm-resolution__results">
        <button v-for="customer in customers" :key="customer.id" type="button" data-testid="select-customer" @click="selectedCustomer = customer">
          <strong>{{ customer.display_name }}</strong>
          <span>{{ customer.kind === 'legal' ? 'Юрлицо' : 'Физлицо' }}</span>
        </button>
      </div>
      <p v-else-if="customerSearchCompleted && !selectedCustomer" class="csm-resolution__hint" data-testid="customer-empty">
        Клиенты не найдены
      </p>
      <div v-if="selectedCustomer" class="csm-resolution__selected">
        <span>Клиент</span><strong>{{ selectedCustomer.display_name }}</strong>
        <button type="button" @click="selectedCustomer = null">Изменить</button>
      </div>
      <div class="csm-resolution__name-grid">
        <label><span>Фамилия</span><input v-model="lastName" autocomplete="family-name" /></label>
        <label><span>Имя</span><input v-model="firstName" autocomplete="given-name" /></label>
        <label><span>Отчество</span><input v-model="middleName" /></label>
        <label><span>Должность</span><input v-model="position" /></label>
      </div>
      <button type="button" class="csm-resolution__primary" data-testid="create-contact-person" :disabled="loading" @click="createPerson">
        {{ loading ? 'Создаём…' : 'Создать и связать' }}
      </button>
    </template>

    <p v-if="errorMessage" class="csm-resolution__error">{{ errorMessage }}</p>
    <button type="button" class="csm-resolution__cancel" @click="emit('cancel')">Отмена</button>
  </section>
</template>

<style scoped src="./CSMContactResolution.css"></style>
